import { filter, find } from 'lodash'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { ProductItemModel } from '@marketplace-web/item-box/product-item-data'
import { GoogleTagManagerEvent, googleTagManagerTrack } from '@marketplace-web/domain/google-track'
import {
  cannotBundleButtonViewEvent,
  ReasonForNotBundling,
} from '@marketplace-web/bundles/items-bundle-data'

import { getIsBundlingAllowed } from '../pages/BundleItems/utils'

type UseItemSelectionProps = {
  selectedItems: Array<ProductItemModel>
  updateSelectedItems: (items: Array<ProductItemModel>) => void
  selectionLimit?: number
  onItemClick?: (item: ProductItemModel, position: number, isSelected: boolean) => void
  items: Array<ProductItemModel>
  sellerId: number
  bundleTrackingId: string | null
  screen: string
}

const useItemSelection = ({
  selectedItems,
  updateSelectedItems,
  selectionLimit,
  onItemClick,
  items,
  sellerId,
  bundleTrackingId,
  screen,
}: UseItemSelectionProps) => {
  const { track } = useTracking()

  const isLimitReached = () => {
    return !!selectedItems.length && selectedItems.length === selectionLimit
  }

  const selectItem = (item: ProductItemModel, index: number) => () => {
    const itemSelected = !!find(selectedItems, { id: item.id })

    if (!itemSelected && isLimitReached()) return

    if (itemSelected) {
      googleTagManagerTrack(GoogleTagManagerEvent.RemoveFromBundle, {
        item_id: item.id,
      })
    }

    const updatedItems = itemSelected
      ? filter(selectedItems, prevItem => prevItem.id !== item.id)
      : [...selectedItems, item]
    const isFirstItemSelected = selectedItems.length === 0 && updatedItems.length === 1

    if (isFirstItemSelected) {
      const cannotBundleEvent = cannotBundleButtonViewEvent({
        listingId: item.id,
        sellerId,
        reasonForNotBundling: ReasonForNotBundling.ShippingSize,
        screen,
        bundleSessionId: bundleTrackingId,
      })
      const selectedItemId = updatedItems[0]?.id

      items.forEach(bundleItem => {
        if (
          !getIsBundlingAllowed({
            item: bundleItem,
            bundleItems: items,
            selectedItemId,
          })
        )
          track(cannotBundleEvent)
      })
    }

    updateSelectedItems(updatedItems)

    if (onItemClick) onItemClick(item, index, !itemSelected)
  }

  return {
    selectItem,
    isLimitReached,
  }
}

export default useItemSelection
